import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { qtyDisplay } from "../../lib/utils";
import { toast } from "sonner";
import { Loader2, ArrowLeft, FileText, Check, X, PenLine, MessageSquare } from "lucide-react";

export default function VetReview() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [order, setOrder] = useState(null);
  const [prescription, setPrescription] = useState(null);
  const [notes, setNotes] = useState("");
  const [modText, setModText] = useState("");
  const [validityDays, setValidityDays] = useState("90");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const loadAll = async () => {
    try {
      const [m, o] = await Promise.all([api.get("/vet/me"), api.get(`/vet/orders/${orderId}`)]);
      setMe(m.data); setOrder(o.data.order); setPrescription(o.data.prescription);
    } catch (e) { setError(formatApiError(e)); }
  };
  useEffect(() => { loadAll(); }, [orderId]); // eslint-disable-line

  const review = async (decision) => {
    if (decision === "modify" && !modText.trim()) {
      toast.error("Describe what needs to be modified"); return;
    }
    if (decision === "approve" && !me?.has_signature) {
      toast.error("Please save your signature in Settings first");
      navigate("/vet/settings"); return;
    }
    setSubmitting(true);
    try {
      await api.post(`/vet/orders/${orderId}/review`, {
        decision, notes,
        modification_request: decision === "modify" ? modText : "",
        prescription_validity_days: decision === "approve" ? (parseInt(validityDays) || null) : null,
      });
      toast.success(decision === "approve" ? "Prescription approved & signed" : decision === "reject" ? "Prescription rejected" : "Modification requested");
      navigate("/vet");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  if (error) return <div className="px-3 sm:px-6 lg:px-10 py-5 sm:py-8 lg:py-10 text-red-400 font-manrope" data-testid="vet-review-error">{error}</div>;
  if (!me || !order) return <div className="px-3 sm:px-6 lg:px-10 py-5 sm:py-8 lg:py-10 flex items-center gap-2 text-white/40 font-manrope text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;

  const rxFileUrl = prescription?.prescription_id
    ? `${process.env.REACT_APP_BACKEND_URL}/api/prescriptions/${prescription.prescription_id}/file`
    : (order?.prescription_path ? `${process.env.REACT_APP_BACKEND_URL}/api/vet/orders/${order.order_id}/prescription` : null);
  const signedRxUrl = order?.signed_prescription_path
    ? `${process.env.REACT_APP_BACKEND_URL}/api/vet/orders/${order.order_id}/prescription/signed`
    : null;
  const isPdf = (prescription?.original_filename || order?.prescription_filename || "").toLowerCase().endsWith(".pdf");
  const reviewed = ["vet_approved", "vet_rejected", "vet_modification_requested"].includes(order.status);
  const inFulfillment = ["shipped", "delivered", "completed", "processing", "sent_to_fulfillment"].includes(order.status);

  return (
    <div className="px-3 sm:px-6 lg:px-10 py-5 sm:py-8 lg:py-10 max-w-6xl" data-testid="vet-review">
      <Link to="/vet" className="flex items-center gap-1.5 text-sm text-white/40 hover:text-white font-manrope mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to queue
      </Link>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left column */}
        <div className="space-y-4">
          <div className="bg-[#142318] border border-white/5 rounded-2xl p-6">
            <p className="font-manrope text-xs uppercase tracking-widest text-white/40 mb-2">Patient</p>
            <h2 className="font-outfit font-bold text-2xl mb-4">{order.pet?.name || "Pet"}</h2>
            <dl className="grid grid-cols-2 gap-y-3 font-manrope text-sm">
              <dt className="text-white/40">Species</dt><dd>{order.pet?.species || ""}</dd>
              <dt className="text-white/40">Age</dt><dd>{order.pet?.age ? `${order.pet.age} ${order.pet.age_unit || ""}` : "n/a"}</dd>
              {order.pet?.medical_notes && <><dt className="text-white/40">Notes</dt><dd className="text-white/70">{order.pet.medical_notes}</dd></>}
            </dl>
          </div>
          {order.pet_medication_info && (
          <div className="bg-[#142318] border border-white/5 rounded-2xl p-6" data-testid="pet-meds-info">
            <p className="font-manrope text-xs uppercase tracking-widest text-white/40 mb-2">Pet Medication / Allergies</p>
            <p className="font-manrope text-sm text-white/80 leading-relaxed whitespace-pre-wrap">{order.pet_medication_info}</p>
          </div>
          )}
          <div className="bg-[#142318] border border-white/5 rounded-2xl p-6">
            <p className="font-manrope text-xs uppercase tracking-widest text-white/40 mb-2">Medication</p>
            <h3 className="font-outfit font-bold text-lg mb-3">{order.medication?.name}</h3>
            <p className="font-manrope text-sm text-white/70">Dosage: <span className="text-white">{order.medication?.dosage}</span></p>
            <p className="font-manrope text-sm text-white/70">Quantity: <span className="text-white">{qtyDisplay(order.medication)}</span></p>
            {order.medication?.notes && <p className="font-manrope text-sm text-white/70 mt-2">{order.medication.notes}</p>}
          </div>
          <div className="bg-[#142318] border border-white/5 rounded-2xl p-6" data-testid="rx-viewer">
            <div className="flex items-center justify-between mb-3">
              <p className="font-manrope text-xs uppercase tracking-widest text-white/40">{signedRxUrl ? "Signed Prescription" : "Prescription Scan"}</p>
              {(signedRxUrl || rxFileUrl) && <a href={signedRxUrl || rxFileUrl} target="_blank" rel="noreferrer" className="text-xs text-emerald-400 hover:underline font-manrope flex items-center gap-1"><FileText className="w-3 h-3" /> Open</a>}
            </div>
            {!rxFileUrl && !signedRxUrl ? (
              <p className="text-white/40 text-sm font-manrope text-center py-8">No prescription file attached.</p>
            ) : (isPdf && !signedRxUrl) ? (
              <a href={signedRxUrl || rxFileUrl} target="_blank" rel="noreferrer" className="block bg-white/5 border border-white/10 rounded-xl py-8 text-center font-manrope text-sm hover:bg-white/10 transition-colors">Open PDF prescription</a>
            ) : (
              <img src={signedRxUrl || rxFileUrl} alt="Prescription" className="rounded-xl border border-white/10 max-h-[520px] w-auto mx-auto"
                onError={(e) => { e.currentTarget.replaceWith(Object.assign(document.createElement("div"), { className: "text-white/40 text-sm font-manrope text-center py-8", textContent: "Could not load image" })); }} />
            )}
          </div>
        </div>
        {/* Right column */}
        <div className="space-y-4">
          <div className="bg-[#142318] border border-white/5 rounded-2xl p-6">
            <p className="font-manrope text-xs uppercase tracking-widest text-white/40 mb-2">Decision</p>
            <h2 className="font-outfit font-bold text-2xl mb-5">Review</h2>
            {reviewed && !inFulfillment ? (
              <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                <p className="font-manrope text-sm text-white/70 mb-2">Status: <span className="font-semibold text-white">{order.status}</span></p>
                {order.vet_review?.signature && <img src={order.vet_review.signature} alt="signature" className="bg-white rounded p-2 max-h-24 mb-3" />}
                {order.vet_review?.modification_request && <p className="font-manrope text-sm text-yellow-300/90"><MessageSquare className="w-3 h-3 inline mr-1" />{order.vet_review.modification_request}</p>}
                {order.vet_review?.notes && <p className="font-manrope text-sm text-white/60 mt-3">&ldquo;{order.vet_review.notes}&rdquo;</p>}
              </div>
            ) : inFulfillment ? (
              <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                <p className="font-manrope text-sm text-white/60">This order is already in fulfillment ({order.status}). Reviewing is locked.</p>
              </div>
            ) : (
              <>
                {me?.has_signature ? (
                  <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-4 mb-4 flex items-start gap-3">
                    <PenLine className="w-4 h-4 text-emerald-400 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-manrope text-sm text-white/80">Your stored signature will be applied on approval.</p>
                      {me.signature && <img src={me.signature} alt="my signature" className="bg-white rounded p-1.5 max-h-16 mt-2" data-testid="vet-stored-signature" />}
                    </div>
                  </div>
                ) : (
                  <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4 mb-4 flex items-start gap-3">
                    <PenLine className="w-4 h-4 text-amber-400 mt-0.5" />
                    <p className="font-manrope text-sm text-white/80">
                      No signature saved. <Link to="/vet/settings" className="text-emerald-400 hover:underline">Go to Settings → Signature</Link> to set one up before approving.
                    </p>
                  </div>
                )}
                <div className="mb-4" data-testid="vet-validity">
                  <label className="font-manrope text-xs text-white/60 uppercase tracking-wider mb-2 block">Prescription valid for (read from the Rx)</label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {[30, 60, 90, 180, 365].map((d) => (
                      <button key={d} type="button" onClick={() => setValidityDays(String(d))} className={`px-3 h-9 rounded-lg font-manrope text-sm border transition-colors ${String(d) === String(validityDays) ? "bg-emerald-500 text-[#0e1812] border-emerald-500" : "bg-white/5 text-white/70 border-white/10 hover:border-white/25"}`}>{d === 365 ? "1 yr" : d === 180 ? "6 mo" : d === 90 ? "3 mo" : d === 60 ? "2 mo" : "1 mo"}</button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="number" min="1" value={validityDays} onChange={(e) => setValidityDays(e.target.value)} className="w-24 h-10 px-3 rounded-xl bg-white/5 border border-white/10 text-sm font-manrope focus:outline-none focus:border-emerald-400 text-right" data-testid="vet-validity-days" />
                    <span className="font-manrope text-sm text-white/50">days. The customer can refill (50% off shipping) until it expires.</span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="font-manrope text-xs text-white/60 uppercase tracking-wider mb-2 block">Notes (optional)</label>
                    <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Clinical notes…" className="w-full p-3 rounded-xl bg-white/5 border border-white/10 text-sm font-manrope focus:outline-none focus:border-emerald-400 resize-none" data-testid="vet-notes" />
                  </div>
                  <div>
                    <label className="font-manrope text-xs text-white/60 uppercase tracking-wider mb-2 block">Modification request (only if requesting changes)</label>
                    <textarea value={modText} onChange={(e) => setModText(e.target.value)} rows={2} placeholder="What needs to change?" className="w-full p-3 rounded-xl bg-white/5 border border-white/10 text-sm font-manrope focus:outline-none focus:border-yellow-400 resize-none" data-testid="vet-modification-text" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-5">
                  <button onClick={() => review("approve")} disabled={submitting} className="h-11 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-[#0e1812] rounded-xl font-manrope font-semibold text-sm flex items-center justify-center gap-1 transition-colors" data-testid="vet-approve">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Approve
                  </button>
                  <button onClick={() => review("modify")} disabled={submitting} className="h-11 bg-yellow-500/20 hover:bg-yellow-500/30 border border-yellow-500/30 disabled:opacity-50 text-yellow-200 rounded-xl font-manrope font-semibold text-sm flex items-center justify-center gap-1 transition-colors" data-testid="vet-modify">
                    <MessageSquare className="w-4 h-4" /> Modify
                  </button>
                  <button onClick={() => review("reject")} disabled={submitting} className="h-11 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 disabled:opacity-50 text-red-200 rounded-xl font-manrope font-semibold text-sm flex items-center justify-center gap-1 transition-colors" data-testid="vet-reject">
                    <X className="w-4 h-4" /> Reject
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
